import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getAuth } from "firebase/auth";
import { useAuthState } from "react-firebase-hooks/auth";
import { Loader2 } from "lucide-react";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from "@stripe/react-stripe-js";
import { app } from "../firebase";
import Sidebar from "./Sidebar";
import Header from "../components/Header";
import QuotePanel from "../components/QuotePanel";
import QuotesAdmin from "../components/QuotesAdmin";
import { Quote, fetchMyQuotes } from "../utils/quotes";
import { trackClick } from "../utils/analytics";
import {
  BillingPlan,
  BillingStatus,
  CheckoutStart,
  LEGACY_PORTAL_URL,
  PLANS,
  fetchBillingStatus,
  openBillingPortal,
  rememberBaseline,
  setBillingMode,
  startCheckout,
  takeBaseline,
  waitForPayment,
} from "../utils/billing";

const card = "bg-white rounded-3xl p-6 shadow-sm border border-slate-100";

const formatDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : "");

const Billing: React.FC = () => {
  const auth = getAuth(app);
  const [user, loading] = useAuthState(auth);
  const [params] = useSearchParams();
  const returnedFromCheckout = params.has("session_id");

  const [status, setStatus] = useState<BillingStatus | null>(null);
  const [quotes, setQuotes] = useState<Quote[]>([]); // custom quotes an admin made for this account
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null); // which action is running
  const [checkout, setCheckout] = useState<CheckoutStart | null>(null);
  const [confirming, setConfirming] = useState<"waiting" | "done" | "slow" | null>(returnedFromCheckout ? "waiting" : null);

  const stripePromise = useMemo(() => (checkout ? loadStripe(checkout.publishableKey) : null), [checkout]);

  const load = useCallback(async () => {
    if (!user) return;
    try {
      setStatus(await fetchBillingStatus(user));
      setError(null);
      fetchMyQuotes(user).then((mine) => setQuotes(mine.quotes)).catch(() => setQuotes([])); // the standard plans still work if this fails
    } catch {
      setError("Could not load your billing information. Please try again in a moment.");
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      if (!returnedFromCheckout) return load();
      // Stripe sent the person back here after paying; the webhook that records it can take a few seconds.
      const baseline = takeBaseline();
      if (!baseline) {
        await load();
        if (!cancelled) setConfirming("slow");
        return;
      }
      const updated = await waitForPayment(user, baseline);
      if (cancelled) return;
      if (updated) setStatus(updated);
      else await load();
      setConfirming(updated ? "done" : "slow");
    })();
    return () => {
      cancelled = true;
    };
  }, [user, returnedFromCheckout, load]);

  const buy = async (plan: BillingPlan) => {
    if (!user || !status) return;
    trackClick(`button_click-billing_${plan}`);
    setBusy(plan);
    setError(null);
    try {
      rememberBaseline(status);
      setCheckout(await startCheckout(user, plan));
    } catch {
      setError("Could not start checkout. Please try again in a moment.");
    } finally {
      setBusy(null);
    }
  };

  const manage = async () => {
    if (!user) return;
    trackClick("button_click-manage_subscription");
    setBusy("manage");
    setError(null);
    try {
      window.location.href = await openBillingPortal(user);
    } catch {
      setError("Could not open the billing portal. Please try again in a moment.");
      setBusy(null);
    }
  };

  const switchMode = async () => {
    if (!user || !status) return;
    setBusy("mode");
    setError(null);
    setCheckout(null);
    try {
      await setBillingMode(user, status.mode === "live" ? "test" : "live");
      await load();
    } catch {
      setError("Could not switch modes.");
    } finally {
      setBusy(null);
    }
  };

  const sub = status?.subscription;

  return (
    <div className="flex flex-col h-screen bg-slate-50">
      <Header />
      <div className="flex flex-1 overflow-hidden">
        <Sidebar activeItem="billing" />
        <main className="flex-1 overflow-y-auto p-4 md:p-8">
          <div className="max-w-3xl mx-auto space-y-8">
            <header>
              <h1 className="text-4xl font-bold text-slate-900 tracking-tight">Billing</h1>
              <p className="text-slate-600 mt-1 text-lg">Buy captioning time or manage your subscription.</p>
            </header>

            {status?.mode === "test" && (
              <div className="bg-amber-50 border border-amber-300 text-amber-900 rounded-2xl p-4" role="status">
                <strong>Test mode.</strong> Nothing here is real. Pay with the test card 4242 4242 4242 4242, any future date and any CVC.
              </div>
            )}

            {confirming === "waiting" && (
              <div className={`${card} flex items-center gap-3`} role="status">
                <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                <span>Thank you. Confirming your payment...</span>
              </div>
            )}
            {confirming === "done" && (
              <div className="bg-green-50 border border-green-300 text-green-900 rounded-2xl p-4" role="status">Payment received. Thank you!</div>
            )}
            {confirming === "slow" && (
              <div className={card} role="status">
                <p>Your payment is being processed. It can take a minute to show up here.</p>
                <button className="btn-secondary mt-3" onClick={load}>Refresh</button>
              </div>
            )}

            {error && <p className="text-red-700" role="alert">{error}</p>}

            {loading || (!status && !error) ? (
              <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            ) : status && (
              <>
                <section className={card} aria-labelledby="status-heading">
                  <h2 id="status-heading" className="text-xl font-bold text-slate-900">Your account</h2>
                  <dl className="mt-3 grid grid-cols-2 gap-4">
                    <div>
                      <dt className="text-slate-600 text-sm">Hours purchased</dt>
                      <dd className="text-3xl font-bold text-slate-900">{status.purchasedHours}</dd>
                    </div>
                    <div>
                      <dt className="text-slate-600 text-sm">{quotes.length > 0 ? "License" : "Monthly plan"}</dt>
                      <dd className="text-lg font-bold text-slate-900">
                        {quotes.length > 0 ? (status.licensed ? "Licensed" : "Not yet paid") : sub?.active ? (sub.cancelAtPeriodEnd ? `Ends ${formatDate(sub.currentPeriodEnd)}` : sub.currentPeriodEnd ? `Active, renews ${formatDate(sub.currentPeriodEnd)}` : "Active") : sub?.status === "past_due" ? "Payment overdue" : "Not subscribed"}
                      </dd>
                    </div>
                  </dl>
                  <div className="mt-4 flex flex-wrap gap-3">
                    {status.hasBillingAccount && (
                      <button className="btn-secondary" onClick={manage} disabled={busy === "manage"}>
                        {busy === "manage" ? "Opening..." : "Manage payment method and invoices"}
                      </button>
                    )}
                    <a className="text-blue-600 underline self-center text-sm" href={LEGACY_PORTAL_URL} target="_blank" rel="noopener noreferrer">
                      Subscribed before this page existed? Manage that subscription
                    </a>
                  </div>
                </section>

                {quotes.length > 0 && user ? (
                  <div className="space-y-4" aria-label="Your quotes">
                    {quotes.map((q) => <QuotePanel key={q.id} user={user} quote={q} onChanged={load} />)}
                  </div>
                ) : checkout && stripePromise ? (
                  <section className={card} aria-label="Checkout">
                    <EmbeddedCheckoutProvider stripe={stripePromise} options={{ clientSecret: checkout.clientSecret }}>
                      <EmbeddedCheckout />
                    </EmbeddedCheckoutProvider>
                    <button className="btn-secondary mt-4" onClick={() => setCheckout(null)}>Cancel</button>
                  </section>
                ) : (
                  <section className="grid sm:grid-cols-2 gap-4" aria-label="Plans">
                    {(Object.keys(PLANS) as BillingPlan[]).map((plan) => (
                      <div key={plan} className={card}>
                        <h2 className="text-xl font-bold text-slate-900">{PLANS[plan].title}</h2>
                        <div className="text-3xl font-bold text-slate-900 mt-2">{PLANS[plan].price}</div>
                        <p className="text-slate-600 text-sm mt-2">{PLANS[plan].detail}</p>
                        <button className="btn-primary mt-4" onClick={() => buy(plan)} disabled={busy !== null || (plan === "monthly" && !!sub?.active)}>
                          {busy === plan ? "Loading..." : plan === "monthly" && sub?.active ? "Subscribed" : plan === "hours" ? "Buy" : "Subscribe"}
                        </button>
                      </div>
                    ))}
                  </section>
                )}

                {status.isAdmin && user && <QuotesAdmin user={user} onChanged={load} />}
                {status.isAdmin && (
                  <section className="bg-slate-100 rounded-2xl p-4 flex items-center justify-between gap-4" aria-label="Admin">
                    <p className="text-sm text-slate-700">
                      <strong>Admin:</strong> payments are in <strong>{status.mode}</strong> mode for your account only. Everyone else is always live.
                    </p>
                    <button className="btn-secondary shrink-0" onClick={switchMode} disabled={busy !== null}>
                      Switch to {status.mode === "live" ? "test" : "live"}
                    </button>
                  </section>
                )}
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Billing;
